/**
 * PrintCalendarDialog — school-admin "Print Calendar" export.
 *
 * Pick up to three months (this month plus the next two) and download ONE
 * PDF: a page per month, per mealtime the school actually serves — services
 * whose menu is identical share one page. The server discovers the mealtimes,
 * so the only other choice is the LANGUAGE (PROD-1293).
 *
 * The language is an explicit choice for the AUDIENCE, not the admin's own UI
 * preference — the calendar is printed and handed to families. It defaults to
 * English, and the caveat under the picker is load-bearing: item names print
 * in whatever language has an APPROVED translation, falling back to English
 * per item, so a Spanish calendar legitimately mixes until the translation
 * queue is cleared.
 *
 * `FormDialog` owns the submit spinner, the inline error, and Enter-to-submit
 * — the render takes a few seconds for a multi-month export.
 */

import { DEFAULT_ORDO_LOCALE, ORDO_LOCALES, type OrdoLocale } from '@ordo/i18n'
import { Checkbox } from '@ordo/ui/checkbox'
import { FormDialog } from '@ordo/ui/form-dialog'
import { Label } from '@ordo/ui/label'
import { RadioGroup, RadioGroupItem } from '@ordo/ui/radio-group'
import { toast } from '@ordo/ui/sonner'
import { type ReactElement, useMemo, useState } from 'react'

import { buildMonthOptions, districtCampuses, downloadMonthCalendarPdf, MONTH_OPTION_COUNT } from '@/api/menu-calendar'
import { type AdminSchool } from '@/api/school-orders'

/** Each language in its OWN language — a Spanish reader should not hunt for "Spanish". */
const LANGUAGE_LABELS: Record<OrdoLocale, string> = {
  'en-US': 'English',
  'es-US': 'Español',
}

export function PrintCalendarDialog({
  school,
  schools,
  open,
  onOpenChange,
}: {
  school: AdminSchool
  /** All the admin's campuses — enables the district ("All campuses") export. */
  schools?: AdminSchool[]
  open: boolean
  onOpenChange: (open: boolean) => void
}): ReactElement {
  const options = useMemo(() => buildMonthOptions(), [])
  const firstMonth = options[0]?.value
  const [selected, setSelected] = useState<Set<string>>(() => new Set(firstMonth ? [firstMonth] : []))
  const [lang, setLang] = useState<OrdoLocale>(DEFAULT_ORDO_LOCALE)
  const campuses = useMemo(() => districtCampuses(school, schools), [school, schools])
  const [scope, setScope] = useState<'all' | 'campus'>('all')
  const allCampuses = campuses.length > 1 && scope === 'all'

  const toggle = (value: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (checked) {
        next.add(value)
      } else {
        next.delete(value)
      }
      return next
    })
  }

  // Keep the picker in the options' order, not click order — the PDF pages
  // run chronologically and the filename span reads from the same list.
  const months = options.map((option) => option.value).filter((value) => selected.has(value))

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Print menu calendar"
      description={`Download a printable menu calendar for ${allCampuses ? 'your campuses' : school.name}. Pick up to ${MONTH_OPTION_COUNT} months — each month prints on its own page, with a page per mealtime served (meals with the same menu share a page).`}
      submitLabel={months.length > 1 ? `Download ${months.length} months` : 'Download'}
      submitDisabled={months.length === 0}
      onSubmit={async () => {
        await downloadMonthCalendarPdf({
          locationId: school.id,
          locationIds: allCampuses ? campuses.map((campus) => campus.id) : undefined,
          schoolName: school.name,
          months,
          lang,
        })
        toast.success(months.length > 1 ? `${months.length} months downloaded` : 'Calendar downloaded')
      }}
      onReset={() => {
        setSelected(new Set(firstMonth ? [firstMonth] : []))
        setLang(DEFAULT_ORDO_LOCALE)
        setScope('all')
      }}>
      {campuses.length > 1 ? (
        <div className="flex flex-col gap-2">
          <Label>Campuses</Label>
          <RadioGroup value={scope} onValueChange={(value) => setScope(value as 'all' | 'campus')}>
            <label className="flex cursor-pointer items-center gap-3 rounded-md border bg-card p-3 text-sm hover:bg-accent/50">
              <RadioGroupItem value="all" />
              <span className="font-medium">All campuses</span>
            </label>
            <label className="flex cursor-pointer items-center gap-3 rounded-md border bg-card p-3 text-sm hover:bg-accent/50">
              <RadioGroupItem value="campus" />
              <span className="font-medium">{school.name} only</span>
            </label>
          </RadioGroup>
          <p className="text-xs text-muted-foreground">
            Campuses that serve the same menu share one calendar. A campus with a different menu gets its own.
          </p>
        </div>
      ) : null}
      <div className="flex flex-col gap-2">
        {options.map((option) => (
          <label
            key={option.value}
            className="flex cursor-pointer items-center gap-3 rounded-md border bg-card p-3 text-sm hover:bg-accent/50">
            <Checkbox
              checked={selected.has(option.value)}
              onCheckedChange={(checked) => toggle(option.value, checked === true)}
            />
            <span className="font-medium">{option.label}</span>
          </label>
        ))}
      </div>
      <div className="flex flex-col gap-2">
        <Label>Language</Label>
        <RadioGroup value={lang} onValueChange={(value) => setLang(value as OrdoLocale)}>
          {ORDO_LOCALES.map((locale) => (
            <label
              key={locale}
              className="flex cursor-pointer items-center gap-3 rounded-md border bg-card p-3 text-sm hover:bg-accent/50">
              <RadioGroupItem value={locale} />
              <span className="font-medium">{LANGUAGE_LABELS[locale]}</span>
            </label>
          ))}
        </RadioGroup>
        <p className="text-xs text-muted-foreground">
          Menu item names print in the language they have an approved translation for, and in English otherwise.
        </p>
      </div>
    </FormDialog>
  )
}
